const loadScript = (src) =>
  new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Loads and opens the appropriate payment gateway SDK based on gatewayOrder.provider returned by backend.
 * Supported providers: "CASHFREE", "RAZORPAY"
 *
 * @param {object} gatewayOrder - Object returned by backend createBooking / initiatePayment / retryPayment
 * @param {string} gatewayOrder.provider - "CASHFREE" | "RAZORPAY"
 * @param {string} [gatewayOrder.paymentSessionId] - Cashfree payment session ID
 * @param {string} [gatewayOrder.paymentLink] - Razorpay order ID or payment link
 * @param {string} [gatewayOrder.orderId] - Order ID
 * @param {number} [gatewayOrder.amount] - Amount in Rupees
 * @param {string} [gatewayOrder.currency] - "INR"
 * @param {string} [gatewayOrder.environment] - "sandbox" | "production"
 * @param {function} [onCancel] - Optional callback if user cancels payment modal
 * @param {function} [onError] - Optional callback if payment fails
 */
export const openPaymentCheckout = async (gatewayOrder, onCancel = null, onError = null) => {
  if (!gatewayOrder) {
    if (onError) onError(new Error("No gateway order provided."));
    throw new Error("No gateway order provided.");
  }

  const provider = (gatewayOrder.provider || "CASHFREE").toUpperCase();

  if (provider === "CASHFREE") {
    const scriptLoaded = await loadScript("https://sdk.cashfree.com/js/v3/cashfree.js");
    if (!scriptLoaded || !window.Cashfree) {
      if (onError) onError(new Error("Failed to load Cashfree payment SDK"));
      throw new Error("Failed to load Cashfree payment SDK. Please check your internet connection.");
    }
    if (!gatewayOrder.paymentSessionId) {
      if (onError) onError(new Error("Payment session ID missing"));
      throw new Error("Payment session ID not returned from backend for Cashfree checkout.");
    }
    const cashfree = window.Cashfree({
      mode: gatewayOrder.environment || "sandbox",
    });
    cashfree.checkout({
      paymentSessionId: gatewayOrder.paymentSessionId,
      redirectTarget: "_self",
    });
  } else if (provider === "RAZORPAY") {
    const scriptLoaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!scriptLoaded || !window.Razorpay) {
      if (onError) onError(new Error("Failed to load Razorpay payment SDK"));
      throw new Error("Failed to load Razorpay payment SDK. Please check your internet connection.");
    }
    const orderId = gatewayOrder.paymentLink || gatewayOrder.orderId || gatewayOrder.id;
    if (!orderId) {
      if (onError) onError(new Error("Order ID missing"));
      throw new Error("Order ID not returned from backend for Razorpay checkout.");
    }

    const keyId = gatewayOrder.keyId || gatewayOrder.key_id;
    if (!keyId) {
      if (onError) onError(new Error("Razorpay key ID missing"));
      throw new Error("Razorpay key ID was not returned by the backend in gatewayOrder.");
    }

    const options = {
      key: keyId,
      order_id: orderId,
      amount: gatewayOrder.amount ? Math.round(gatewayOrder.amount * 100) : undefined, // paise
      currency: gatewayOrder.currency || "INR",
      name: "GoPratle",
      description: "Booking Payment",
      retry: {
        enabled: false,
      },
      handler: async function (response) {
        try {
          // Send signature to backend verification endpoint
          const baseUrl = process.env.REACT_APP_SERVER_BASE_URL || "http://localhost:5000/api";
          const cleanBaseUrl = baseUrl.replace(/\/+$/, "");
          
          const verifyRes = await fetch(`${cleanBaseUrl}/verify-payment`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id || orderId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });

          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            window.location.href = `/payment/success?order_id=${gatewayOrder.orderId || orderId}`;
          } else {
            alert(`Payment verification failed: ${verifyData.message || "Invalid signature"}`);
            if (onError) onError(verifyData);
          }
        } catch (err) {
          console.error("Verification endpoint error:", err);
          // Still redirect so backend webhook/status check can confirm
          window.location.href = `/payment/success?order_id=${gatewayOrder.orderId || orderId}`;
        }
      },
      modal: {
        ondismiss: function () {
          console.log("Razorpay checkout modal closed by user.");
          if (onCancel) onCancel();
        },
      },
      prefill: {
        name: gatewayOrder.customerName || "",
        email: gatewayOrder.customerEmail || "",
        contact: gatewayOrder.customerPhone || "",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", function (response) {
      console.error("Razorpay Payment Failed:", response.error);
      if (onError) {
        onError(response.error);
      }
      // Redirect to payment failure page so our system renders the failure state with our custom Retry button
      const targetOrderId = gatewayOrder.orderId || orderId;
      window.location.href = `/payment/failed?order_id=${targetOrderId}`;
    });
    rzp.open();
  } else {
    if (onError) onError(new Error(`Unsupported payment provider: ${provider}`));
    throw new Error(`Unsupported payment provider: ${provider}`);
  }
};
